import React, { useState, useEffect } from "react";
import { CSSTransition } from "react-transition-group";
import { FaCashRegister } from "react-icons/fa";
import { GoAlertFill } from "react-icons/go";
import "./index.css"; 

import { useCash } from "../../../../hooks/useCash";
import { useOffice } from "../../../../hooks/useOffice";
import { useRequests } from "../../../../hooks/useRequests";
import { getUser, formatCurrency} from "../../../../utils/Common";
import Swal from "sweetalert2";
import CajaCardContainer from "../../../../components/ui/CajaCard/CajaCardContainer";
import Spinner from "../../../../components/Spinner";
import NotifyC from "../../../../components/Notifications";
import { useFirebaseRealtime } from "../../../../hooks/useFirebaseRealtime";

const GestionCaja = () => {
    const { getCashBoxStatus, getCashBoxMovements, openCloseCash } = useCash();
    const { getPostOfficeStatus } = useOffice();
    const { createRequest } = useRequests();    
    const [InfoCash, setInfoCash] = useState([]);
    const [isLoading, setIsLoading] = useState({});
    const [isLoadingOpt, setIsLoadingOpt] = useState({});
    const [currentBalances, setCurrentBalances] = useState({});
    const [newBalance, setNewBalance] = useState({});
    const [isEditing, setIsEditing] = useState({});
    const [alertMessage, setAlertMessage] = useState("");
    const [saldoSucursal, setSaldoSucursal] = useState(0);
    const [saldoTotalCajas, setSaldoTotalCajas] = useState(0);
    const [pendingRequests, setPendingRequests] = useState({});
    const [saldoEstafeta, setSaldoEstafeta] = useState(null);
    const [estafetasData, setEstafetasData] = useState([]);
    const [isLoadingEstafetas, setIsLoadingEstafetas] = useState(false);
    const [hasRealtimeUpdate, setHasRealtimeUpdate] = useState(false);
    const [lastUpdateTime, setLastUpdateTime] = useState(null);
    const user = getUser();
    const { data: realtimeData, loading, error } = useFirebaseRealtime('cash');

    const handleGetInfoOffices = async () => {
        const abortController = new AbortController();
        const response = await getPostOfficeStatus(abortController.signal);
        if (response?.payload?.length > 0) {
        const saldoTotal = response.payload.reduce(
            (acc, sucursal) => acc + (sucursal.SaldoSucursal || 0),
            0
        );
        setSaldoEstafeta(saldoTotal);
        }
    };

    const handleGetEstafetasData = async () => {
        if (user?.roleId !== 9) return;
        
        setIsLoadingEstafetas(true);
        try {
            const abortController = new AbortController();
            const response = await getPostOfficeStatus(abortController.signal);
            if (response?.payload?.length > 0) {
                setEstafetasData(response.payload);
            }
        } catch (error) {
            console.error("Error fetching estafetas data:", error);
        } finally {
            setIsLoadingEstafetas(false);
        }
    };
        
    const handleRequestStatusUpdate = (cashId, status) => {
        if (status === 2 || status === 3 || status === 4) {
            setPendingRequests(prev => ({ ...prev, [cashId]: status }));
        }
    };

    const handleGetinfoCashBox = async (signal) => {
        try {
            const response = await getCashBoxStatus(signal);
            const cashMovements = await getCashBoxMovements(signal, [3, 4, 5]);
            const pendingRequestsData = {}; 
            const updatedInfoCash = response?.cajas?.map(caja => {
                const movement = cashMovements.payload.find(mov => mov.IdCaja === caja.IdCaja);

                const hasPendingRequest = movement?.EstatusSolicitud === 1;
                pendingRequestsData[caja.IdCaja] = hasPendingRequest;

                if (!hasPendingRequest && movement?.EstatusSolicitud){
                handleRequestStatusUpdate(caja.IdCaja, movement.EstatusSolicitud);
                }

                return {
                    ...caja,
                    IdTipoMovimiento: movement?.IdTipoMovimiento,
                    FechaMovimientoCajeroType: movement?.FechaMovimientoCajeroType,
                    MontoMovimiento: movement?.MontoMovimiento,
                    UltimoSaldoCierre: movement?.SaldoAnteriorCierre,
                    UltimoSaldoApertura: movement?.SaldoAnteriorApertura,
                    UltimoSaldoAgregado: movement?.SaldoAnteriorAgregado,
                };
            }) || [];
            setInfoCash(updatedInfoCash);
            const balances = updatedInfoCash.reduce((acc, caja) => {
                acc[caja.IdCaja] = caja.SaldoCaja;
                return acc;
            }, {});
            setSaldoSucursal(updatedInfoCash[0]?.SaldoSucursal ?? 0);
            setSaldoTotalCajas(response.totalCajas ?? 0);
            setCurrentBalances(balances);
            setPendingRequests(pendingRequestsData); 
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error("Error fetching cash box status:", error);
            }
        }
    };

    const validateBalance = async (balance, caja) => {
        const numericBalance = parseFloat(balance);
    
        if (isNaN(numericBalance)) {
            setAlertMessage("Por favor, ingresa un saldo válido.");
            setIsLoading(prev => ({ ...prev, [caja.IdCaja]: false }));
            return false;
        }
    
        let comparisonBalance;
        let comparisonMessage;
    
        if (caja.EstatusCaja === 1) { 
            comparisonBalance = caja.UltimoSaldoApertura ?? 0;
            comparisonMessage = `El saldo actual de caja (<strong>${formatCurrency(numericBalance)}</strong>) no coincide con el último saldo de apertura de la caja (<strong>${formatCurrency(comparisonBalance)}</strong>). <br> ¿Quiere usted proceder igualmente?`;
        } else { 
            comparisonBalance = caja.UltimoSaldoCierre ?? 0;
            comparisonMessage = `El saldo actual de caja (<strong>${formatCurrency(numericBalance)}</strong>) no coincide con el último saldo de cierre de la caja (<strong>${formatCurrency(comparisonBalance)}</strong>). <br> ¿Quiere usted proceder igualmente?`;
        }
    
        if (Number(comparisonBalance) !== numericBalance) {
            const result = await Swal.fire({
                title: "¡Atención!",
                html: comparisonMessage,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "white",
                cancelButtonColor: "black",
                confirmButtonText: "Aceptar",
                cancelButtonText: "Cancelar",
                customClass: {
                    confirmButton: 'custom-confirm-button',
                    cancelButton: 'custom-cancel-button',
                },
            });
    
            return result.isConfirmed;
        }
    
        return true;
    };

    const handleErrorResponse = (response) => {
        if (response.code >= 400) {
            switch (response.message) {
                case "CASHBOX_ALREADY_OPEN":
                    return {
                        title: "¡La caja ya está abierta!",
                        text: "Actualizando estado de la caja..."
                    };
                case "CASHBOX_ALREADY_CLOSED":
                    return {
                        title: "¡La caja ya está cerrada!",
                        text: "Actualizando estado de la caja..."
                    };
                case "MUST_GENERATE_REPORT_BEFORE_OPENING":
                    return {
                        title: "¡Informe de cierre de caja requerido!",
                        text: "Se deben generar los informes de cierre de caja de todos los movimientos realizados para poder proceder."
                    };
                case "REPORT_TOTALS_DO_NOT_MATCH_TRANSACTIONS":
                    return {
                        title: "¡Diferencia en los totales!",
                        text: "Por favor, verifica que se hayan generado todos los reportes de cierre de caja en el ultimo turno."
                    };
                case "CASHBOX_AMOUNT_NOT_MATCH":
                    return {
                        title: "¡Diferencia en el saldo de caja!",
                        text: "Por favor, actualiza el saldo de la caja para continuar."
                    };
                case "CASHBOX_OPEN_CANNOT_PERFORM_ACTION":
                    return {
                        title: "¡Caja abierta!",
                        text: "No se puede realizar esta acción mientras la caja esté abierta."
                    };
                case "CASHBOX_NOT_ACTIVE":
                    return {
                        title: "¡Caja desactivada!",
                        text: "Por favor, contacta al administrador para activar la caja."
                    };
                default:
                    return {
                        title: "Ops! Algo salió mal.",
                        text: "Por favor, inténtalo de nuevo."
                    };
            }
        }
        return {
            title: "Ops! Algo salió mal.",
            text: "Por favor, inténtalo de nuevo."
        };
    };      

    const handleOpenCloseCash = async (typeMov, caja) => {
      try {
        setIsLoading(prev => ({ ...prev, [caja.IdCaja]: true }));
        const balanceToUse = currentBalances[caja.IdCaja] === "" ? caja.SaldoCaja : currentBalances[caja.IdCaja];
        const verifyMov = await validateBalance(balanceToUse, caja);
        if (!verifyMov) {
          setIsLoading(prev => ({ ...prev, [caja.IdCaja]: false }));
          return;
        }
  
        const abortController = new AbortController();
        const response = await openCloseCash(abortController.signal, { typeMov, amount: Number(balanceToUse), idCashBox: caja.IdCaja });
        setIsLoading(prev => ({ ...prev, [caja.IdCaja]: false }));
        if (response.code === 200) {
          Swal.fire({
            title: "¡Operación completada!",
            text: typeMov === 1 ? "Se abrió la caja correctamente" : "Se cerró la caja correctamente",
            icon: "success",
            confirmButtonText: "Ok",
            customClass: {
              confirmButton: 'custom-confirm-button',
            },
          });
          setCurrentBalances(prev => ({ ...prev, [caja.IdCaja]: "" }));
          setAlertMessage("");
          handleGetinfoCashBox();
        } else {
          setCurrentBalances(prev => ({ ...prev, [caja.IdCaja]: "" }));
          const errorResponse = handleErrorResponse(response);
          Swal.fire({
            title: errorResponse.title,
            text: errorResponse.text,
            icon: 'warning',
            showCancelButton: false,
            timer: 3000,
            timerProgressBar: true,
            didOpen: () => {
                Swal.showLoading();
                handleGetinfoCashBox();
            },
          });
          } 
      } catch (error) {
        setIsLoading(prev => ({ ...prev, [caja.IdCaja]: false }));
        setCurrentBalances(prev => ({ ...prev, [caja.IdCaja]: "" }));
        NotifyC.error("Ops! Algo salió mal. Por favor, inténtalo de nuevo.");
      }
    };

    const handleCashTransaction = async (cash, amount, type) => {
        const abortController = new AbortController();
        try {
            const [cashBoxResponse, officeResponse] = await Promise.all([
                getCashBoxStatus(abortController.signal),
                getPostOfficeStatus(abortController.signal)
            ]);

            const cajaActualizada = cashBoxResponse?.cajas?.find(c => c.IdCaja === cash.IdCaja);
            const saldoCajaActual = cajaActualizada?.SaldoCaja ?? cash.SaldoCaja;
            // Filtrar solo la estafeta específica de la caja
            const estafetaCaja = officeResponse?.payload?.find(sucursal => sucursal.IdSucursal === cash.IdSucursal);
            const saldoEstafetaActual = estafetaCaja?.SaldoSucursal ?? 0;

            await handleGetinfoCashBox();
            await handleGetInfoOffices();

            // Validaciones

            if (cash.EstatusCaja === 1) {
                NotifyC.error("No se pueden realizar solicitudes de agregar, retirar o anular saldo mientras la caja esté abierta. Por favor, cierre la caja primero.");
                return false;
            }

            if (type === 3 && saldoEstafetaActual !== null && Number(amount) > saldoEstafetaActual) {
                NotifyC.error("El monto a añadir no puede ser mayor al saldo de la estafeta.");
                return false;
            }
            if (type === 4 && Number(amount) > saldoCajaActual) {
                NotifyC.error("El monto a retirar no puede ser mayor al saldo de la caja.");
                return false;
            }
            const Amount = Number(amount);
            if (isNaN(Amount) || Amount <= 0) {
                NotifyC.error("Debe ingresar un monto válido.");
                return false;
            }

            const confirmMessage = type === 3
                ? `¿Desea confirmar el monto de <strong>${formatCurrency(Amount)}</strong> para <strong>añadir</strong> a esta caja?`
                : type === 4
                ? `¿Desea confirmar el monto de <strong>${formatCurrency(Amount)}</strong> para <strong>retirar</strong> de esta caja?`
                : `¿Está seguro de que desea anular el último monto añadido de <strong>${formatCurrency(Amount)}</strong> ?`;

            const result = await Swal.fire({
                title: "¡Atención!",
                html: confirmMessage,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "white",
                cancelButtonColor: "black",
                confirmButtonText: "Aceptar",
                cancelButtonText: "Cancelar",
                customClass: {
                    confirmButton: 'custom-confirm-button',
                    cancelButton: 'custom-cancel-button',
                },
            });

            if (!result.isConfirmed) return false;

            setIsLoadingOpt(prev => ({ ...prev, [cash.IdCaja]: true }));
            const dataToSend = {
                IdTipoSolicitud: 2,
                IdEstatus: 1,
                IdCaja: cash.IdCaja,
                IdSucursal: cash.IdSucursal,
                Accion: type,
                SaldoIngresado: Amount,
                IdProvincia: cash.IdProvincia,
            };

            const response = await createRequest(abortController.signal, dataToSend);

            const successMessages = {
                3: "Tu solicitud ha sido enviada para añadir el monto.",
                4: "Tu solicitud ha sido enviada para retirar el monto.",
                5: "Tu solicitud ha sido enviada para anular el monto.",
            };
            const errorMessages = {
                "ACTION_TYPE_INVALID": "Tipo de acción inválido.",
                "CLIENT_NOT_FOUND_IN_SYSTEM": "Usuario no encontrado en el sistema.",
                "OFFICE_NOT_FOUND": "Estafeta no encontrada.",
                "OFFICE_NOT_ACTIVE": "La estafeta de la caja se encuentra desactivada. Por favor, contacta al administrador para activar la estafeta.",
                "CASHBOX_NOT_FOUND": "Caja no encontrada, intentelo de nuevo.",
                "CASHBOX_NOT_IN_OFFICE": "La caja no pertenece a la estafeta.",
                "ERROR_CREATING_REQUEST": "Error al crear la solicitud.",
                "CASHBOX_HAS_PENDING_REQUEST": "La caja tiene una solicitud pendiente.",
                "CASHBOX_OPEN_CANNOT_PERFORM_ACTION": "No se puede realizar esta acción mientras la caja esté abierta.",
                "CASHBOX_NOT_ACTIVE": "La caja se encuentra desactivada. Por favor, contacta al administrador para activar la caja.",
            };

            if (response?.code === 200) {
                setNewBalance(prev => ({ ...prev, [cash.IdCaja]: "" }));
                Swal.fire({
                    title: '¡Solicitud Registrada!',
                    text: successMessages[type] || 'Solicitud completada.',
                    icon: "success",
                    confirmButtonText: "Ok",
                    customClass: { confirmButton: 'custom-confirm-button' }
                });
                handleGetinfoCashBox();
                handleGetInfoOffices();
            } else {
                Swal.fire({
                    title: 'Error!',
                    text: errorMessages[response?.message] || 'Hubo un error desconocido.',
                    icon: 'error',
                    confirmButtonText: "Ok",
                    customClass: { confirmButton: "custom-confirm-button" }
                });
                handleGetinfoCashBox();
                handleGetInfoOffices();
            }
            return true;
        } catch (error) {
            Swal.fire('Error!', `Hubo un error al procesar la solicitud`, 'error');
            handleGetinfoCashBox();
            handleGetInfoOffices();
            return false;
        } finally {
            setIsLoadingOpt(prev => ({ ...prev, [cash.IdCaja]: false }));
        }
    };
  
    const handleReloadCashStatus = async (cashId) => {
        setIsLoading(prev => ({ ...prev, [cashId]: true }));
        await handleGetinfoCashBox();
        setIsLoading(prev => ({ ...prev, [cashId]: false }));
        Swal.fire({
          title: 'Actualizado!',
          text: 'El estado de la caja ha sido actualizado.',
          icon: 'success',
          timer: 1500, 
          showConfirmButton: false, 
        });
    };


    const handleNewBalanceChange = (e, idCaja) => {
        const value = e.target.value;
        const regex = /^\d*(\.\d{0,2})?$/;

        if (regex.test(value)) {
            setNewBalance(prev => ({ ...prev, [idCaja]: value }));
        }
    };

     const LoadingSpinner = () => {
        return (
            <div
                style={{
                    position: "absolute",
                    top: 0,
                    padding: 20,
                    right: 0,
                    left: 0,
                    backgroundColor: "rgb(0 0 0 / 81%)",
                    height: "100vh",
                    zIndex: 1000,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "black",
                }}
                className="text-left"
            >
                <Spinner type={1} size={40} color="white" />
            </div>
        );
    };

    useEffect(() => {
      const abortController = new AbortController();
      handleGetinfoCashBox(abortController.signal);

      return () => abortController.abort(); 
  }, []);

    useEffect(() => {
        handleGetInfoOffices();
    }, []);

    useEffect(() => {
        handleGetEstafetasData();
    }, [user?.roleId]);

    useEffect(() => {
        if (!realtimeData || InfoCash.length === 0) return;
        
        setInfoCash(prevCash => {
            let hasChanges = false;
            
            const updatedCash = prevCash.map(caja => {
                const firebaseUpdate = realtimeData[caja.IdCaja];
                
                if (firebaseUpdate) {
                    const changed = 
                        (firebaseUpdate.status !== null && caja.EstatusCaja !== firebaseUpdate.status) ||
                        (firebaseUpdate.balance !== null && caja.SaldoCaja !== firebaseUpdate.balance) ||
                        (firebaseUpdate.hasPendingRequest !== null && caja.hasPendingRequest !== firebaseUpdate.hasPendingRequest);

                    if (changed) {
                        hasChanges = true;                        
                        return {
                            ...caja, 
                            EstatusCaja: firebaseUpdate.status !== null ? firebaseUpdate.status : caja.EstatusCaja,
                            SaldoCaja: firebaseUpdate.balance !== null ? firebaseUpdate.balance : caja.SaldoCaja,
                            hasPendingRequest: firebaseUpdate.hasPendingRequest !== null ? firebaseUpdate.hasPendingRequest : caja.hasPendingRequest,
                            _realtimeUpdate: true 
                        };
                    }
                }
                
                return caja; 
            });
            
            if (hasChanges) {
                const newPendingRequests = updatedCash.reduce((acc, caja) => {
                    if (caja.hasPendingRequest !== undefined && caja._realtimeUpdate) {
                        acc[caja.IdCaja] = caja.hasPendingRequest;
                    }
                    return acc;
                }, {});
                
                if (Object.keys(newPendingRequests).length > 0) {
                    setPendingRequests(prev => ({ ...prev, ...newPendingRequests }));
                }
                
                setTimeout(() => {
                    setInfoCash(currentCash => 
                        currentCash.map(caja => ({ ...caja, _realtimeUpdate: false }))
                    );
                }, 100);
                
                return updatedCash;
            }
            
            return prevCash;
        });
    }, [realtimeData]);

    return (
        <> 
             <div className="dashboardCard text-left" style={{ color: "black", marginBottom: "20px" }}>
            <div className="d-flex justify-content-between" style={{ gap: 20 }}>
                <div className="d-flex align-items-center">
                    <FaCashRegister size={20} className="mr-2" />
                    <h5 className="mb-0">Gestión de Caja</h5>
                </div>
                <div className="d-flex align-items-center">
                    <div
                        className="text-center mt-lg-0 mt-sm-3"
                        style={{
                            fontSize: "1rem",
                            background: "#dbf5f3",
                            padding: "8px 12px",
                            borderRadius: "12px",
                        }}
                    >
                        <div style={{ color: "#0eab9b" }}>
                            {user?.roleId >= 4 ? "Saldo de estafetas" : "Saldo de estafeta"}
                        </div>
                        {saldoEstafeta === null ? (
                            <div style={{
                                display: "flex",
                                justifyContent: "center",
                                gap: 5,
                                marginTop: 5,
                                marginBottom: 5
                            }}>
                                <Spinner type={0} size={10} color={"#0eab9b"} />
                                <Spinner type={0} size={10} color={"#0eab9b"} />
                                <Spinner type={0} size={10} color={"#0eab9b"} />
                            </div>
                        ) : (
                            <div className="text-bold" style={{ color: "#0eab9b" }}>
                                {formatCurrency(saldoEstafeta)}
                            </div>
                        )}
                    </div>
                    <div
                        className="text-center mt-lg-0 mt-sm-3 ml-2"
                        style={{
                            fontSize: "1rem",
                            background: "#dbf5f3",
                            padding: "8px 12px",
                            borderRadius: "12px",
                        }}
                    >
                        <div style={{ color: "#0eab9b" }}>
                            Saldo en operación
                        </div>
                        {saldoTotalCajas === null ? (
                            <div style={{
                                display: "flex",
                                justifyContent: "center",
                                gap: 5,
                                marginTop: 5,
                                marginBottom: 5
                            }}>
                                <Spinner type={0} size={10} color={"#0eab9b"} />
                                <Spinner type={0} size={10} color={"#0eab9b"} />
                                <Spinner type={0} size={10} color={"#0eab9b"} />
                            </div>
                        ) : (
                            <div className="text-bold" style={{ color: "#0eab9b" }}>
                                {formatCurrency(saldoTotalCajas)}
                            </div>
                        )}
                    </div>
                </div>
                
            </div>
        </div>

        <div className="gestion-caja">
            {InfoCash.length === 0 ? (
                  <LoadingSpinner />
            ) : (
                <CajaCardContainer
                    cajas={InfoCash}
                    isLoading={isLoading}
                    rotate={false}
                    handleCashTransaction={handleCashTransaction}
                    handleOpenCloseCash={handleOpenCloseCash}
                    handleReloadCashStatus={handleReloadCashStatus}
                    handleNewBalanceChange={handleNewBalanceChange}
                    newBalance={newBalance}
                    showAddWithdrawButtons={true}
                    roleBli={user.roleId}
                    pendingRequests={pendingRequests}
                    estafetasData={estafetasData}
                    isLoadingEstafetas={isLoadingEstafetas}
                    handleGetEstafetasData={handleGetEstafetasData}
                />
            )}
        </div>
          
              <CSSTransition
                in={!!alertMessage}
                timeout={300}
                classNames="alert"
                unmountOnExit
              >
                <div className="p-4">
                  <div className="banner-alert-cash">
                    <div className="banner-message">
                      <GoAlertFill className="mr-2" size={20} />
                      {alertMessage}
                    </div>
                    <div
                      className="banner-close-btn"
                      onClick={() => setAlertMessage("")}
                    >
                      ✖
                    </div>
                  </div>
                </div>
              </CSSTransition>
       </>
    );
   
};
export default GestionCaja;